import { failure, success } from '@mntn/result'
import type { GaryClient } from '../../client/index.ts'
import type {
  Logout,
  LogoutVariables,
  Select_GetUserById,
  Select_GetUserByIdVariables,
  Select_MainSession,
  Select_MainSessionVariables,
  Select_PtvSession,
  Select_PtvSessionCookie,
  Select_PtvSessionCookieVariables,
  Select_PtvSessionVariables,
} from '../../graphql/generated.types.ts'
import { mutations, queries } from './user.graphql.ts'
import { isRoleName, type PtvUser } from './user.schema.ts'

export type { RoleScope } from '../../graphql/generated.types.ts'

export type UserGateway = ReturnType<typeof createUserGateway>

export const createUserGateway = (client: GaryClient) => ({
  /** @deprecated - Remove this gary call from flight-deck. Use select principal instead */
  getSession: async () => {
    const { data } = await client.query<Select_MainSession, Select_MainSessionVariables>({
      query: queries.MAIN_SESSION,
      fetchPolicy: 'no-cache',
    })

    return data.session
  },

  /** This queries minimal fields. Exists just to get the Set-Cookie header. */
  getPtvSessionCookie: async (opts: { onResponseHeaders?: (headers: Headers) => void }) => {
    try {
      const { data, error } = await client.query<Select_PtvSessionCookie, Select_PtvSessionCookieVariables>({
        query: queries.PTV_SESSION_COOKIE,
        fetchPolicy: 'no-cache',
        context: {
          onResponseHeaders: opts.onResponseHeaders,
        },
      })

      if (!data.session) {
        return failure(
          {
            customCode: 'PTV_SESSION_NOT_FOUND_ERROR',
            message: 'Ptv session not found',
          },
          {
            graphqlError: JSON.stringify(error),
            query: queries.PTV_SESSION_COOKIE,
          },
        )
      }

      const me = data.session

      return success(me, { event: 'gary_client.user.get_ptv_session_cookie' })
    } catch (error) {
      return failure(
        {
          customCode: 'PTV_SESSION_NOT_FOUND_ERROR',
          message: 'Unable to fetch ptv session cookie',
        },
        {
          query: queries.PTV_SESSION_COOKIE,
          cause: error,
        },
      )
    }
  },

  getPtvSession: async () => {
    try {
      const { data, error } = await client.query<Select_PtvSession, Select_PtvSessionVariables>({
        query: queries.PTV_SESSION,
        fetchPolicy: 'no-cache',
      })

      if (!data.session) {
        return failure(
          {
            customCode: 'PTV_SESSION_NOT_FOUND_ERROR',
            message: 'Ptv session not found',
          },
          {
            graphqlError: JSON.stringify(error),
            query: queries.PTV_SESSION,
          },
        )
      }

      const me = data.session

      const formattedMe = {
        userId: me.user_id,
        emailAddress: me.email_addr,
        firstName: me.first_name,
        lastName: me.last_name,
        advertisers:
          me.advertisers
            ?.filter(advertiser => advertiser !== null)
            .map(advertiser => ({
              advertiserId: advertiser.advertiser_id,
              companyName: advertiser.company_name,
              logo: advertiser.logo,
            })) ?? [],
        currentAdvertiser: me.current_advertiser
          ? {
              advertiserId: me.current_advertiser.advertiser_id,
              companyName: me.current_advertiser.company_name,
              logo: me.current_advertiser.logo,
            }
          : null,
      }

      return success(formattedMe, { event: 'gary_client.user.get_ptv_session' })
    } catch (error) {
      return failure(
        {
          customCode: 'PTV_SESSION_NOT_FOUND_ERROR',
          message: 'Error fetching ptv session',
        },
        {
          query: queries.PTV_SESSION,
          cause: error,
        },
      )
    }
  },

  getUserById: async ({ userId }: { userId: number }) => {
    try {
      const { data } = await client.query<Select_GetUserById, Select_GetUserByIdVariables>({
        query: queries.GET_USER_BY_ID,
        variables: { userId },
      })

      const user = data.getUserById

      if (!user?.role) {
        return failure({
          customCode: 'USER_NOT_FOUND_ERROR',
          message: 'User not found',
        })
      }

      const cleanDomain = (url?: string | null) =>
        url
          ?.replace(/^https?:\/\//, '')
          .replace(/^www\./i, '')
          .split('/')[0] ?? ''

      // Note: string & {} is for type discrimination
      const roleName: string & {} = user.role.name

      const transformedUser: PtvUser = {
        id: user.id,
        emailAddr: user.email_addr,
        role: {
          name: isRoleName(roleName) ? roleName : roleName,
        },
        currentAdvertiser: user.current_advertiser
          ? {
              advertiserId: user.current_advertiser.advertiser_id,
              companyName: user.current_advertiser.company_name ?? '',
              domain: cleanDomain(user.current_advertiser.company_url),
              logo: user.current_advertiser.logo ?? null,
              lastLoginTimeForUser: user.current_advertiser.last_login_time_for_user ?? null,
            }
          : null,
        advertisers:
          user.advertisers
            ?.filter(advertiser => advertiser !== null)
            .map(advertiser => ({
              advertiserId: advertiser.advertiser_id,
              companyName: advertiser.company_name,
              logo: advertiser.logo ?? null,
              domain: cleanDomain(advertiser.company_url),
              lastLoginTimeForUser: advertiser.last_login_time_for_user ?? null,
            })) ?? [],
      }

      return success(transformedUser, {
        event: 'gary_client.user.get_user_by_id',
        userId,
      })
    } catch (error) {
      return failure(
        { customCode: 'USER_GATEWAY_QUERY_ERROR', message: 'User not found' },
        {
          cause: error,
        },
      )
    }
  },

  logout: async () => {
    const { data } = await client.mutate<Logout, LogoutVariables>({
      mutation: mutations.LOGOUT,
    })

    return data?.logout
  },

  switchAdvertiser: async ({
    onResponseHeaders,
    advertiserId,
  }: {
    onResponseHeaders: (headers: Headers) => void
    advertiserId: number
  }) => {
    try {
      await client.mutate({
        mutation: mutations.SWITCH_ADVERTISER,
        variables: { advertiserId },
        context: { onResponseHeaders },
      })
      return success({ data: { advertiserId } }, { event: 'gary_client.user.switch_advertiser' })
    } catch (error) {
      return failure(
        {
          customCode: 'USER_GATEWAY_QUERY_ERROR',
          message: 'Error switching advertiser',
        },
        {
          cause: error,
          event: 'gary_client.user.switch_advertiser',
          payload: { advertiserId },
        },
      )
    }
  },
})
